import { Button, Text, Flex, Box } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { ModalVoucherProps, SharedModalProps } from 'types/Modal';
import { FilesIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useRedeemVoucher } from 'hooks/api/backend/voucher/useRedeemVoucher';
import { useUser } from 'utils/auth';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { SubscriptionStatus, VoucherUsageStatusType } from '@novaheal/types';
import { ToastVariant } from 'types/Toast';
import { VoucherStatusType } from 'types/StatusTypes';
import { toastContent } from 'config/toast';
import { Form, RHFFormInput } from 'components/common/Form/v2';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import Joi from 'joi';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

const schema = Joi.object({
  shortCode: Joi.string().required().messages({
    'string.empty': errorMessage.empty,
  }),
});

type VoucherInput = {
  shortCode: string;
};

type Props = SharedModalProps & ModalVoucherProps;

export const VoucherModal = ({ handleClose, isShown, onSuccess }: Props): ReactElement => {
  const redeemVoucher = useRedeemVoucher();
  const { refetch: refetchUser } = useUser();
  const toast = useNovaToast();

  const showToast = (
    status: VoucherUsageStatusType,
    previousSubscriptionStatus: SubscriptionStatus | undefined
  ): void => {
    const isSuccess = status === VoucherUsageStatusType.SUCCESS;
    const toastVariant = isSuccess ? ToastVariant.SUCCESS : ToastVariant.ERROR;
    const toastStatus =
      status === VoucherUsageStatusType.SUCCESS &&
      previousSubscriptionStatus === SubscriptionStatus.PAID
        ? VoucherStatusType.PREVIOUSLY_PAID_SUB
        : status;

    toast.show({
      title: toastContent.voucher[toastStatus].title,
      description: toastContent.voucher[toastStatus].description,
      variant: toastVariant,
    });
  };
  const handleSubmit = async (values: VoucherInput): Promise<void> => {
    const { shortCode } = values;
    const { status: voucherCodeStatus, previousSubscriptionStatus } =
      await redeemVoucher.mutateAsync(shortCode);
    if (voucherCodeStatus === VoucherUsageStatusType.SUCCESS) {
      handleClose();
    }
    showToast(voucherCodeStatus, previousSubscriptionStatus);
    const { data: user } = await refetchUser();
    if (onSuccess && user) {
      onSuccess(user?.b2bSchool);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" size="md">
      <CustomModalContent
        display="flex"
        gap="30px"
        flexDirection="column"
        alignItems="center"
        p="40px 30px"
      >
        <Flex direction="column" gap="15px" textAlign="center">
          <Text textStyle="h4.medium" color="text.primary">
            Organisations-Lizenz aktivieren
          </Text>
          <Text textStyle="body.m.regular" color="text.tertiary">
            Den Code erhältst Du von deiner Organisation. Schalte alle Lerninhalte kostenlos frei.
          </Text>
        </Flex>
        <Box width="100%">
          <Form<VoucherInput, typeof schema> onSubmit={handleSubmit} schema={schema}>
            <Flex flexDirection="column" gap="4xl" alignItems="center" pt="5px" width="100%">
              <RHFFormInput
                type="text"
                name="shortCode"
                leftElement={
                  <NovaIcon icon={FilesIcon} size="s" color="icon.quaternary" aria-hidden />
                }
                placeholder="Organisationslizenz"
              />
              <Flex gap="l" justifyContent="flex-end" width="100%">
                <Button variant="tertiary" onClick={handleClose} w={{ base: 'full', md: '220px' }}>
                  Abbrechen
                </Button>
                <Button type="submit" variant="primary" w={{ base: 'full', md: '220px' }}>
                  Aktivieren
                </Button>
              </Flex>
            </Flex>
          </Form>
        </Box>
      </CustomModalContent>
    </CustomModal>
  );
};
